/**
 * Local IndexedDB cache of push notifications.
 *
 * Role after Phase C (2026-06-03): offline fallback only. The bell
 * badge and the /notifications route both query the server first;
 * IDB is used only when the network call fails. The SW continues
 * to write here on every push (foreground only — background pushes
 * are surfaced via the DriverNotification table on the server side).
 */
import { db } from './idb';

const MAX_CACHED = 50;

export type CachedPushNotification = {
  id: string;
  title: string;
  body: string;
  receivedAt: number;
  readAt?: number;
  link?: string;
};

export async function cachePushNotification(msg: {
  title: string;
  body: string;
  data?: Record<string, string>;
}): Promise<void> {
  const handle = await db();
  const id = `push-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await handle.put('pushNotifications', {
    id,
    title: msg.title,
    body: msg.body,
    receivedAt: Date.now(),
    link: msg.data?.url,
  });

  const all = await handle.getAll('pushNotifications');
  if (all.length > MAX_CACHED) {
    const sorted = all.sort((a, b) => b.receivedAt - a.receivedAt);
    const toDelete = sorted.slice(MAX_CACHED);
    const tx = handle.transaction('pushNotifications', 'readwrite');
    await Promise.all(toDelete.map((row) => tx.objectStore('pushNotifications').delete(row.id)));
    await tx.done;
  }
}

export async function listCachedPushNotifications(): Promise<CachedPushNotification[]> {
  const handle = await db();
  const all = await handle.getAll('pushNotifications');
  return all.sort((a, b) => b.receivedAt - a.receivedAt);
}

export async function markPushRead(id: string): Promise<void> {
  const handle = await db();
  const row = await handle.get('pushNotifications', id);
  if (!row) return;
  await handle.put('pushNotifications', { ...row, readAt: Date.now() });
}
